import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Layout, Menu, Button, Popconfirm, Modal, Tooltip } from "antd";
import {
  HomeOutlined,
  MessageFilled,
  PlayCircleOutlined,
  PoweroffOutlined,
  SettingOutlined,
  UsergroupAddOutlined,
  UserAddOutlined,
  AuditOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  LockOutlined,
  ThunderboltOutlined,
  BellOutlined,
  FileTextOutlined,
  MessageOutlined,
  ProductOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import "./SideNav.css"; // Import the CSS file for custom styles
import { logout } from "../../store/slices/profile.slice.js";
import { getLoginPathForTenantContext } from "../../utils/tenantFromHost.js";
import { useAppSelector } from "../../hooks/reduxHooks.js";

import InviteAgentsForm from "../../pages/Inviteuser/inviteuser.component.jsx";
import { FcDocument } from "react-icons/fc";
const { Sider } = Layout;

const getStoredNavPermissions = () => {
  try {
    return JSON.parse(localStorage.getItem("nav_permissions") || "[]");
  } catch {
    return [];
  }
};

const SideNav = ({ collapsed, setCollapsed }) => {
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
  const [selectedKey, setSelectedKey] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userData } = useAppSelector((state) => state.profile);

  // Track mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close sidebar on mobile when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only apply on mobile screens
      if (isMobile && !collapsed) {
        const sider = document.querySelector('.custom-sider');
        if (sider && !sider.contains(event.target)) {
          setCollapsed(true);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [collapsed, setCollapsed, isMobile]);
  // Set the selected key based on the current path
  useEffect(() => {
    if (
      location.pathname === "/knowledge-base" ||
      location.pathname.startsWith("/knowledge-base/")
    ) {
      setSelectedKey("/knowledge-base");
    } else if (
      location.pathname === "/resolve-conflict" ||
      location.pathname.startsWith("/resolve-conflict/")
    ) {
      setSelectedKey("/knowledge-base");
    } else {
      setSelectedKey(location.pathname);
    }
  }, [location.pathname]);

  const showInviteModal = () => {
    setIsInviteModalVisible(true);
  };
  const handleInviteModalClose = () => {
    setIsInviteModalVisible(false);
  };

  // Get role from local storage
  const userRole = userData?.role || localStorage.getItem("role");
  const currentUsername = userData?.username || localStorage.getItem("username") || "User";

  const handleLogout = () => {
    const slug = localStorage.getItem("slug");
    dispatch(logout());
    navigate(getLoginPathForTenantContext({ slug }), { replace: true });
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  // Close sidebar on mobile after navigation
  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setCollapsed(true);
    }
  };

  const allowed_navs = Array.isArray(userData?.nav_permissions)
    ? userData.nav_permissions
    : getStoredNavPermissions();

  return (
    <>
      {/* Mobile overlay */}
      {!collapsed && isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            zIndex: 999,
          }}
          onClick={() => setCollapsed(true)}
        />
      )}
      <Sider
        collapsible
        collapsed={collapsed}
        trigger={null}
        className={`custom-sider ${collapsed ? 'collapsed' : ''}`}
        width={200}
        collapsedWidth={80}
      >
      <div
        className="logo-container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0px",
        }}
      >
        <Button
          className="collapse-button tour-toggle"
          icon={
            collapsed ? (
              <MenuUnfoldOutlined className="menu-icon" />
            ) : (
              <MenuFoldOutlined className="menu-icon" />
            )
          }
          onClick={toggleCollapsed}
        >
          {!collapsed && <span style={{ fontWeight: "bold" }}>Menu</span>}
        </Button>
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        className={collapsed ? "custom-menu collapsed-menu" : "custom-menu"}
      >
        {allowed_navs.includes("Dashboard") && (
          <Menu.Item
            key="/dashboard"
            icon={<HomeOutlined className="menu-icon" />}
            onClick={() => handleNavigation("/dashboard")}
            className="menu-item"
            data-menu-id="/dashboard"
          >
            Dashboard
          </Menu.Item>
        )}

        {allowed_navs.includes("Channels") && (
          <Menu.Item
            key="/channels"
            icon={<MessageOutlined className="menu-icon" />}
            onClick={() => handleNavigation("/channels")}
            className="menu-item"
            data-menu-id="/channels"
          >
            Channels
          </Menu.Item>
        )}

        {allowed_navs.includes("KnowledgeBase") && (
          <Menu.Item
            key="/knowledge-base"
            icon={<FileTextOutlined className="menu-icon" />}
            onClick={() => handleNavigation("/knowledge-base")}
            className="menu-item"
            data-menu-id="/knowledge-base"
          >
            Knowledge Base
          </Menu.Item>
        )}

        {allowed_navs.includes("Playground") && (
          <Menu.Item
            key="/playground"
            icon={<PlayCircleOutlined className="menu-icon" />}
            onClick={() => handleNavigation("/playground")}
            className="menu-item"
            data-menu-id="/playground"
          >
            Playground
          </Menu.Item>
        )}

        {allowed_navs.includes("Products") && (
          <Menu.Item
            key="/products"
            icon={<ProductOutlined className="menu-icon" />}
            onClick={() => handleNavigation("/products")}
            className="menu-item"
            data-menu-id="/products"
          >
            Products
          </Menu.Item>
        )}

        {allowed_navs.includes("Feedback Log") && (
          <Menu.Item
            key="/feedback-log"
            icon={<AuditOutlined className="menu-icon" />}
            onClick={() => handleNavigation("/feedback-log")}
            className="menu-item"
            data-menu-id="/feedback-log"
          >
            Feedback Log
          </Menu.Item>
        )}

        {allowed_navs.includes("CTA") && (
          <Menu.Item
            key="/cta"
            icon={<ThunderboltOutlined className="menu-icon" />}
            onClick={() => handleNavigation("/cta")}
            className="menu-item"
            data-menu-id="/cta"
          >
            Call To Action
          </Menu.Item>
        )}

        {allowed_navs.includes("Setup") && (
          <Menu.Item
            key="/setup"
            icon={<SettingOutlined className="menu-icon" />}
            onClick={() => handleNavigation("/setup")}
            className="menu-item"
            data-menu-id="/setup"
          >
            Setup
          </Menu.Item>
        )}

        {userRole !== "agent" && allowed_navs.includes("User Registration") && (
          <Menu.Item
            className="menu-item"
            icon={<UserAddOutlined />}
            onClick={showInviteModal}
            data-menu-id="/invite-user"
          >
            Invite User
          </Menu.Item>
        )}

        {allowed_navs.includes("FAQ") && (
          <Menu.Item
            key="/faq"
            icon={<QuestionCircleOutlined className="menu-icon" />}
            onClick={() => handleNavigation("/faq")}
            className="menu-item"
            data-menu-id="/faq"
          >
            FAQ
          </Menu.Item>
        )}

        {allowed_navs.includes("Settings") && (
          <Menu.Item
            key="/settings"
            icon={<SettingOutlined className="menu-icon" />}
            onClick={() => handleNavigation("/settings")}
            className="menu-item"
            data-menu-id="/settings"
          >
            Settings
          </Menu.Item>
        )}

        {/* Logout as a Menu.Item with Popconfirm */}
        <Popconfirm
          title={
            <span>
              Hey <b>{currentUsername}</b>! Are you sure you want to log out?
            </span>
          }
          onConfirm={handleLogout}
          okText="Yes"
          cancelText="No"
        >
          <Menu.Item
            key="logout"
            icon={<PoweroffOutlined className="menu-icon" />}
            className="menu-item"
            style={{
              position: "absolute",
              bottom: "1vh",
              width: "calc(100% - 8px)",
            }}
          >
            Logout
          </Menu.Item>
        </Popconfirm>
      </Menu>

      <Modal
        title="Invite a new User"
        open={isInviteModalVisible}
        onCancel={handleInviteModalClose}
        footer={null}
        width={800}
        destroyOnClose
      >
        <InviteAgentsForm onClose={handleInviteModalClose} />
      </Modal>
    </Sider>    </>  );
};

export default SideNav;
